import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCOP, getMinPrice } from '@/utils/format';
import { resolveImageUrl } from '@/utils/images';
import { useCart } from '@/context/CartContext';

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const [imgError, setImgError] = useState(false);
  const minPrice = getMinPrice(product.variants);
  const hasStock = product.variants?.some((v) => v.stock > 0);
  const firstVariant = product.variants?.[0];

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (firstVariant) addItem(product, firstVariant, 1);
  };

  const getTag = () => {
    if (product.is_specialized) return { label: 'Especializado', color: 'bg-[#D47A4A]/10 text-[#D47A4A]' };
    if (product.is_holistic) return { label: 'Holístico', color: 'bg-[#2A5C43]/10 text-[#2A5C43]' };
    if (product.is_grain_free) return { label: 'Libre de granos', color: 'bg-[#8BA58F]/20 text-[#2A5C43]' };
    if (product.life_stage?.includes('cachorro') || product.life_stage?.includes('gatito')) return { label: 'Cachorros', color: 'bg-[#F5E6D3] text-[#8B4513]' };
    if (product.life_stage?.includes('senior')) return { label: 'Senior', color: 'bg-[#D8C5A3]/40 text-[#6B5D3F]' };
    return null;
  };

  const tag = getTag();

  return (
    <Link
      to={`/producto/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="product-card group relative bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden flex flex-col"
    >
      {/* Image */}
      <div className="relative aspect-square bg-[#F7F2E8] overflow-hidden">
        {product.images?.[0] && !imgError ? (
          <img
            src={resolveImageUrl(product.images[0], 500)}
            alt={product.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#2A5C43] p-4" data-testid={`product-noimage-${product.slug}`}>
            <span className="font-display font-bold text-sm sm:text-base text-center leading-tight tracking-wide uppercase">{product.brand}</span>
            <span className="text-[10px] sm:text-xs text-[#8BA58F] mt-1 text-center line-clamp-2">{product.name}</span>
          </div>
        )}
        {tag && (
          <span className={`absolute top-2 left-2 sm:top-3 sm:left-3 text-[9px] sm:text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full ${tag.color}`}>
            {tag.label}
          </span>
        )}
        {!hasStock && (
          <span className="absolute top-2 right-2 sm:top-3 sm:right-3 text-[9px] sm:text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-red-100 text-red-700">
            Agotado
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-3 sm:p-5">
        <p className="text-[9px] sm:text-[10px] tracking-[0.15em] uppercase text-[#8BA58F] font-semibold mb-0.5 sm:mb-1 truncate">
          {product.brand}
        </p>
        <h3 className="font-display font-semibold text-[#1C1917] text-sm sm:text-base leading-snug mb-1 line-clamp-2">
          {product.name}
        </h3>
        <p className="hidden sm:block text-xs text-[#57534E] mb-3">
          {product.protein && <span>{product.protein}</span>}
          {product.life_stage?.length > 0 && (
            <span className="ml-1 capitalize">• {product.life_stage[0]}</span>
          )}
        </p>

        {product.variants?.length > 1 && (
          <p className="text-[10px] sm:text-[11px] text-[#57534E] mb-2 sm:mb-3">
            {product.variants.length} <span className="sm:hidden">tamaños</span><span className="hidden sm:inline">presentaciones disponibles</span>
          </p>
        )}

        <div className="mt-auto flex items-end justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] tracking-wider uppercase text-[#8BA58F]">Desde</p>
            <p className="font-display font-bold text-base sm:text-lg text-[#2A5C43] truncate">{formatCOP(minPrice)}</p>
          </div>
          <button
            onClick={handleQuickAdd}
            disabled={!hasStock}
            aria-label="Agregar al carrito"
            data-testid={`product-quick-add-${product.slug}`}
            className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-full bg-[#2A5C43] hover:bg-[#214A35] disabled:bg-[#E7E5E4] disabled:cursor-not-allowed flex items-center justify-center text-white"
          >
            <ShoppingCart className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
